import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Info, ChevronDown, ChevronRight, Loader2, ArrowLeft, Download, Eye, History, Copy, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import ReportPreview, { RPT_STATUS_BG } from "@/components/ReportPreview";
import ReportApprovals from "@/components/ReportApprovals";

const MANDATORY = ["cover", "executive_summary", "evidence_source_overview", "priority_insights", "approved_actions", "limitations", "source_references"];
const OPTIONAL_APPENDICES = ["intelligence_questions", "academic_community", "appendices", "appendix_themes", "appendix_scoring", "appendix_sources", "appendix_validation"];
const ARCHIVABLE = ["Draft", "Under scientific review", "Revision required"];

function useReturnFocus() {
  const [returnTo] = useState(() => document.activeElement);
  return (e) => { e.preventDefault(); returnTo?.focus?.(); };
}

function ArchiveDialog({ report, onClose, onDone }) {
  const restoreFocus = useReturnFocus();
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [deps, setDeps] = useState(null);
  const [depsError, setDepsError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [archiveError, setArchiveError] = useState("");
  const loadDeps = () => {
    setDepsError(false);
    api.get(`/reports/registry/${report.id}/dependencies`).then((r) => setDeps(r.data)).catch(() => setDepsError(true));
  };
  useEffect(loadDeps, [report.id]);
  const confirm = () => {
    if (reason.trim().length < 5) { setReasonError("An archive reason of at least 5 characters is required."); return; }
    setBusy(true); setArchiveError("");
    api.post(`/reports/registry/${report.id}/archive`, { reason })
      .then((r) => { onDone(r.data); toast.success(`${report.id} archived — restorable at any time.`); onClose(); })
      .catch((e) => setArchiveError(e.response?.data?.detail || "Archive failed — please retry."))
      .finally(() => setBusy(false));
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="archive-dialog" aria-busy={busy} onCloseAutoFocus={restoreFocus}>
        <DialogHeader>
          <DialogTitle className="font-heading text-base">Archive report — {report.id}</DialogTitle>
          <DialogDescription className="text-xs text-slate-600 leading-snug">
            Soft archive of "{report.title}". The report remains restorable at any time; dependencies and version history are retained.
          </DialogDescription>
        </DialogHeader>
        {!deps && !depsError && (
          <p className="text-[11px] text-slate-400 flex items-center gap-1.5" role="status" aria-live="polite" data-testid="archive-deps-loading">
            <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />Loading dependency snapshot…
          </p>
        )}
        {depsError && (
          <p className="text-[11px] text-red-600 flex items-center gap-2" role="alert" data-testid="archive-deps-error">
            Dependency snapshot could not be loaded.
            <Button variant="outline" className="h-6 text-[10px]" onClick={loadDeps} data-testid="archive-deps-retry-btn">Retry</Button>
          </p>
        )}
        {deps && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-[11px] text-slate-600" data-testid="archive-dependencies">
            <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400 mb-1">Dependencies retained</p>
            <p>{deps.sections} sections · {deps.exports_recorded} recorded export(s) · {deps.versions} version(s){deps.linked_claims.length > 0 && ` · ${deps.linked_claims.length} linked claims`}</p>
            <p className="text-slate-400 mt-1">{deps.note}</p>
          </div>
        )}
        <div>
          <label htmlFor="archive-reason" className="font-mono text-[9px] uppercase tracking-wide text-slate-400 block mb-1">Archive reason (required)</label>
          <textarea id="archive-reason" value={reason} onChange={(e) => { setReason(e.target.value); if (reasonError) setReasonError(""); }} rows={2} data-testid="archive-reason-input"
            placeholder="e.g. superseded by v4, sandbox test complete…"
            aria-required="true" aria-invalid={!!reasonError} aria-describedby={reasonError ? "archive-reason-error" : undefined}
            className={`w-full text-xs border rounded-lg p-2.5 focus:outline-none focus:border-teal-400 ${reasonError ? "border-red-300" : "border-slate-200"}`} />
          {reasonError && <p id="archive-reason-error" className="text-[10.5px] text-red-600 mt-1" role="alert" data-testid="archive-reason-error">{reasonError}</p>}
        </div>
        {archiveError && (
          <p className="text-[10.5px] text-red-600" role="alert" data-testid="archive-error-message">{archiveError} <button className="underline font-semibold" onClick={confirm} data-testid="archive-retry-btn">Retry</button></p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" className="h-8 text-xs" onClick={onClose} data-testid="archive-cancel-btn">Cancel</Button>
          <Button className="h-8 text-xs bg-slate-900 text-white hover:bg-slate-800" disabled={busy} onClick={confirm} data-testid="archive-confirm-btn">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" aria-hidden="true" />}{busy ? "Archiving…" : "Archive (soft — restorable)"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const TEMPLATES = [
  { id: "exec", name: "Executive Intelligence Report", type: "Executive Intelligence Report",
    audience: "Senior leadership, cross-functional decision-makers",
    purpose: "Headline assessment, priority insights and reviewed strategic direction for decision support.",
    formats: ["PDF", "PPTX", "DOCX", "CSV"], updated: "2026-06-20",
    recommended: ["adoption", "consensus_controversies", "evidence_gaps", "competitive_intelligence", "recommendations"] },
  { id: "medical", name: "Medical Affairs Report", type: "Medical Affairs Briefing",
    audience: "Global, regional and local Medical Affairs teams",
    purpose: "HCP Voice synthesis, clinical themes, segment and geographic variation, field implications.",
    formats: ["PDF", "DOCX"], updated: "2026-06-20",
    recommended: ["clinical_themes", "academic_community", "geographic_variation", "evidence_to_voice", "evidence_gaps"] },
  { id: "evidence", name: "Evidence and Scientific Report", type: "Scientific Intelligence Report",
    audience: "Clinical Development, Evidence Generation, HEOR, scientific reviewers",
    purpose: "Methodology, source landscape, evidence quality, Evidence-to-Voice alignment and gaps.",
    formats: ["PDF", "DOCX", "CSV"], updated: "2026-06-20",
    recommended: ["objectives", "source_landscape", "evidence_to_voice", "consensus_controversies", "evidence_gaps"] },
  { id: "custom", name: "Custom Report", type: "Custom Report",
    audience: "Authorised users requiring a configurable structure",
    purpose: "Configurable structure built on the governed section catalogue.",
    formats: ["PDF", "PPTX", "DOCX", "CSV"], updated: "2026-06-20",
    recommended: ["objectives", "source_landscape", "clinical_themes", "adoption", "geographic_variation", "evidence_to_voice", "consensus_controversies", "competitive_intelligence", "evidence_gaps", "recommendations"] },
];

const AUDIENCES = ["Senior leadership", "Medical Affairs", "Field Medical (MSL)", "Commercial / Brand", "Market Access", "Clinical Development", "Scientific reviewers"];
const SCOPES = ["Global", "North America", "Europe", "APAC", "Global (NA, EU, APAC)"];
const FUNCTIONS = ["Medical Affairs", "Commercial / Brand", "Market Access", "Competitive Intelligence", "Clinical Development", "Field Medical / MSL"];

function GovernancePopover() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="text-slate-400 hover:text-teal-600" data-testid="governance-info-btn" title="Reporting governance" aria-label="Show reporting governance rules"><Info className="w-3.5 h-3.5" aria-hidden="true" /></button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="start" data-testid="governance-popover">
        <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1.5">Reporting governance</p>
        {["Only reporting-approved content enters a final report",
          "AI-assisted text remains labelled until scientifically reviewed",
          "Every material finding retains source traceability",
          "Post-cut-off evidence (after 14 Jun 2026) is excluded from the locked assessment unless explicitly disclosed"].map((t) => (
          <p key={t} className="text-[11px] text-slate-600 flex gap-1.5 py-0.5"><span className="text-teal-500 shrink-0">•</span>{t}</p>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function CreateReportModal({ reg, template, onClose, onCreated }) {
  const restoreFocus = useReturnFocus();
  const [step, setStep] = useState(template ? 1 : 0);
  const [tpl, setTpl] = useState(template || null);
  const [form, setForm] = useState({ title: "", audience: "", scope: "Global", functions: [], output_format: "PDF", detail: "Standard" });
  const [adv, setAdv] = useState(false);
  const [advOpts, setAdvOpts] = useState({ min_confidence: "Low", min_review: "Scientifically reviewed", statements: true, charts: true, reviewer_commentary: false, citation_style: "Vancouver" });
  const [secs, setSecs] = useState([]);
  const [optOpen, setOptOpen] = useState(false);
  const [reorder, setReorder] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [createError, setCreateError] = useState("");
  const catalog = reg.sections_catalog;
  const titleOf = (id) => catalog.find((s) => s.id === id)?.title || id;

  const pickTpl = (t) => {
    setTpl(t);
    setSecs([...MANDATORY, ...t.recommended].map((id) => ({ id, on: true, locked: MANDATORY.includes(id) })));
    setForm((f) => ({ ...f, title: t.id === "custom" ? "" : `${t.name} — Elacestrant Global Assessment`, audience: t.audience.split(",")[0] }));
    setStep(1);
  };
  useEffect(() => { if (template) pickTpl(template); /* eslint-disable-next-line */ }, []);

  const move = (i, dir) => setSecs((arr) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return arr;
    const n = [...arr]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });

  const create = () => {
    setBusy(true); setCreateError("");
    api.post("/reports/registry", {
      title: form.title, type: tpl.type, purpose: tpl.purpose, audience: form.audience || tpl.audience,
      scope: form.scope, functions: form.functions, output_format: form.output_format,
      citation_style: advOpts.citation_style, sections: secs.filter((s) => s.on).map((s) => s.id),
      section_config: { detail: form.detail, ...advOpts },
    }).then((r) => { toast.success(`${r.data.id} created as a draft — scientific review required before approval`); onCreated(r.data); onClose(); })
      .catch((e) => setCreateError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Report creation failed — please retry."))
      .finally(() => setBusy(false));
  };

  const validateStep1 = () => {
    const errs = {};
    if (!form.title.trim()) errs.title = "A report title is required.";
    if (!form.audience) errs.audience = "An intended audience is required.";
    setErrors(errs);
    if (errs.title) { document.querySelector('[data-testid="create-title"]')?.focus(); return false; }
    if (errs.audience) { document.querySelector('[data-testid="create-audience"]')?.focus(); return false; }
    return true;
  };

  const toggleFn = (f) => setForm((x) => ({ ...x, functions: x.functions.includes(f) ? x.functions.filter((y) => y !== f) : [...x.functions, f] }));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="create-report-modal" aria-busy={busy} onCloseAutoFocus={restoreFocus}>
        <DialogHeader>
          <DialogTitle className="font-heading text-base">Create report</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Three-step wizard: choose a report template, tailor its content and sections, then review and create a draft for scientific review.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1.5 mb-2" role="list" aria-label="Wizard progress">
          {["Choose report", "Tailor content", "Review and create"].map((s, i) => (
            <span key={s} role="listitem" aria-current={i === step ? "step" : undefined} className={`text-[9.5px] font-mono uppercase tracking-wide rounded-full px-2 py-0.5 border ${i === step ? "bg-slate-900 text-white border-slate-900" : i < step ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-slate-50 text-slate-400 border-slate-200"}`} data-testid={`create-step-${i}`}>{i + 1}. {s}</span>
          ))}
        </div>

        {step === 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {TEMPLATES.map((t) => (
              <button key={t.id} onClick={() => pickTpl(t)} data-testid={`template-${t.id}`}
                className="text-left border border-slate-200 rounded-xl p-3.5 hover:border-teal-400 transition-colors">
                <p className="text-[13px] font-bold text-slate-900">{t.name}</p>
                <p className="text-[10.5px] text-slate-500 mt-0.5">{t.purpose}</p>
                <p className="text-[10px] text-slate-400 mt-1.5">{t.audience}</p>
                <p className="text-[9.5px] font-mono text-slate-400 mt-1">{MANDATORY.length + t.recommended.length} sections · {t.formats.join(" / ")}</p>
              </button>
            ))}
          </div>
        )}

        {step === 1 && tpl && (
          <div className="space-y-3">
            <div>
              <Input placeholder="Report title *" value={form.title} onChange={(e) => { setForm({ ...form, title: e.target.value }); if (errors.title) setErrors((x) => ({ ...x, title: undefined })); }}
                data-testid="create-title" className={`text-sm ${errors.title ? "border-red-300" : ""}`} aria-label="Report title" aria-required="true"
                aria-invalid={!!errors.title} aria-describedby={errors.title ? "create-title-error" : undefined} />
              {errors.title && <p id="create-title-error" className="text-[10.5px] text-red-600 mt-1" role="alert" data-testid="create-title-error">{errors.title}</p>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Select value={form.audience} onValueChange={(v) => { setForm({ ...form, audience: v }); if (errors.audience) setErrors((x) => ({ ...x, audience: undefined })); }}>
                  <SelectTrigger className={`text-sm ${errors.audience ? "border-red-300" : ""}`} data-testid="create-audience" aria-label="Intended audience" aria-required="true"
                    aria-invalid={!!errors.audience} aria-describedby={errors.audience ? "create-audience-error" : undefined}><SelectValue placeholder="Intended audience *" /></SelectTrigger>
                  <SelectContent>{AUDIENCES.map((a) => <SelectItem key={a} value={a} className="text-xs">{a}</SelectItem>)}</SelectContent>
                </Select>
                {errors.audience && <p id="create-audience-error" className="text-[10.5px] text-red-600 mt-1" role="alert" data-testid="create-audience-error">{errors.audience}</p>}
              </div>
              <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                <SelectTrigger className="text-sm" data-testid="create-scope" aria-label="Geographic scope"><SelectValue /></SelectTrigger>
                <SelectContent>{SCOPES.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.output_format} onValueChange={(v) => setForm({ ...form, output_format: v })}>
                <SelectTrigger className="text-sm" aria-label="Output format"><SelectValue /></SelectTrigger>
                <SelectContent>{tpl.formats.map((f) => <SelectItem key={f} value={f} className="text-xs">{f}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.detail} onValueChange={(v) => setForm({ ...form, detail: v })}>
                <SelectTrigger className="text-sm" aria-label="Content detail level"><SelectValue /></SelectTrigger>
                <SelectContent>{["Concise", "Standard", "Detailed"].map((d) => <SelectItem key={d} value={d} className="text-xs">Content detail: {d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400 mb-1">Relevant functions</p>
              <div className="flex flex-wrap gap-1.5">
                {FUNCTIONS.map((f) => (
                  <button key={f} onClick={() => toggleFn(f)} data-testid={`fn-${f.split(" ")[0].toLowerCase()}`}
                    className={`text-[10.5px] rounded-full px-2.5 py-1 border ${form.functions.includes(f) ? "bg-teal-50 text-teal-700 border-teal-300" : "bg-white text-slate-500 border-slate-200"}`}>{f}</button>
                ))}
              </div>
            </div>
            <p className="text-[10px] text-slate-400 bg-slate-50 border border-slate-200 rounded-lg p-2">
              Prepopulated from the project (no re-entry): ORSERDU · elacestrant · ER+/HER2−, ESR1-mutated advanced BC · Cross-sectional assessment · Evidence cut-off 14 Jun 2026 · Prepared by MarksMan HealthCare (demonstration).</p>

            <div>
              <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400 mb-1">Sections</p>
              <p className="text-[10.5px] text-slate-500 mb-1.5">Core sections (required, locked): {MANDATORY.map(titleOf).join(" · ")}</p>
              <div className="space-y-1">
                {secs.filter((s) => !s.locked && !OPTIONAL_APPENDICES.includes(s.id)).map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-[11.5px] text-slate-700 cursor-pointer" data-testid={`sec-${s.id}`}>
                    <input type="checkbox" checked={s.on} onChange={(e) => setSecs((arr) => arr.map((x) => x.id === s.id ? { ...x, on: e.target.checked } : x))} className="accent-teal-600" />
                    {titleOf(s.id)}<span className="text-[9px] text-slate-400">recommended</span>
                  </label>
                ))}
              </div>
              <button onClick={() => setOptOpen(!optOpen)} className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 mt-1.5" data-testid="optional-appendices-toggle">
                {optOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}Optional appendices
              </button>
              {optOpen && (
                <div className="space-y-1 mt-1">
                  {OPTIONAL_APPENDICES.map((id) => {
                    const cur = secs.find((s) => s.id === id);
                    return (
                      <label key={id} className="flex items-center gap-2 text-[11.5px] text-slate-600 cursor-pointer">
                        <input type="checkbox" checked={!!cur?.on} onChange={(e) => setSecs((arr) => cur ? arr.map((x) => x.id === id ? { ...x, on: e.target.checked } : x) : [...arr, { id, on: true, locked: false }])} className="accent-teal-600" />
                        {titleOf(id)}
                      </label>
                    );
                  })}
                </div>
              )}
              {tpl.id === "custom" && (
                <button onClick={() => setReorder(!reorder)} className="text-[11px] font-semibold text-teal-700 mt-2" data-testid="reorder-toggle">{reorder ? "Done reordering" : "Reorder sections"}</button>
              )}
              {reorder && secs.map((s, i) => (
                <div key={s.id} className="flex items-center gap-2 text-[11px] text-slate-600 py-0.5">
                  <button onClick={() => move(i, -1)} className="text-slate-400">↑</button>
                  <button onClick={() => move(i, 1)} className="text-slate-400">↓</button>
                  {titleOf(s.id)}{!s.on && <span className="text-slate-300">(excluded)</span>}
                </div>
              ))}
            </div>

            <button onClick={() => setAdv(!adv)} className="flex items-center gap-1 text-[11px] font-semibold text-slate-500" data-testid="advanced-options-toggle">
              {adv ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}Advanced options
            </button>
            {adv && (
              <div className="grid grid-cols-2 gap-2">
                <Select value={advOpts.min_confidence} onValueChange={(v) => setAdvOpts({ ...advOpts, min_confidence: v })}>
                  <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Low", "Moderate", "High"].map((x) => <SelectItem key={x} value={x} className="text-xs">Min confidence: {x}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={advOpts.min_review} onValueChange={(v) => setAdvOpts({ ...advOpts, min_review: v })}>
                  <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Under scientific review", "Scientifically reviewed", "Approved for reporting"].map((x) => <SelectItem key={x} value={x} className="text-xs">Min review: {x}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={advOpts.citation_style} onValueChange={(v) => setAdvOpts({ ...advOpts, citation_style: v })}>
                  <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{reg.citation_styles.map((c) => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}</SelectContent>
                </Select>
                <div className="flex flex-col gap-1 text-[11px] text-slate-600">
                  {[["statements", "Representative statements"], ["charts", "Charts"], ["reviewer_commentary", "Reviewer commentary"]].map(([k, l]) => (
                    <label key={k} className="flex items-center gap-1.5 cursor-pointer"><input type="checkbox" checked={advOpts[k]} onChange={(e) => setAdvOpts({ ...advOpts, [k]: e.target.checked })} className="accent-teal-600" />{l}</label>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-between">
              <Button variant="outline" className="h-8 text-xs" onClick={() => setStep(0)} data-testid="create-back-btn">Back</Button>
              <Button className="h-8 text-xs bg-slate-900 text-white hover:bg-slate-800" onClick={() => validateStep1() && setStep(2)} data-testid="create-next">Continue</Button>
            </div>
          </div>
        )}

        {step === 2 && tpl && (
          <div className="space-y-3">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px] text-slate-600" data-testid="create-summary">
              {[["Report type", tpl.type], ["Title", form.title], ["Audience", form.audience], ["Scope", form.scope],
                ["Evidence cut-off", "14 Jun 2026"], ["Sections included", secs.filter((s) => s.on).length],
                ["Content eligibility", `Min review: ${advOpts.min_review} · min confidence: ${advOpts.min_confidence}`],
                ["Expected output", `${form.output_format} (${form.detail})`]].map(([l, v]) => (
                <p key={l}><span className="font-mono text-[9px] uppercase text-slate-400 block">{l}</span>{v}</p>
              ))}
            </div>
            <p className="text-[10.5px] text-amber-700">Readiness note: AI-assisted content enters the draft only; approval and locking require the review workflow and a passing readiness check.</p>
            {createError && (
              <p className="text-[10.5px] text-red-600" role="alert" data-testid="create-error-message">{createError} <button className="underline font-semibold" onClick={create} data-testid="create-retry-btn">Retry</button></p>
            )}
            <div className="flex justify-between">
              <Button variant="outline" className="h-8 text-xs" onClick={() => setStep(1)} data-testid="create-back-to-step1">Back</Button>
              <Button className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white" disabled={busy} onClick={create} data-testid="create-draft-btn">
                {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" aria-hidden="true" />}{busy ? "Creating draft…" : "Create draft report"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ExportBlockerDialog({ report, onClose }) {
  const restoreFocus = useReturnFocus();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const load = () => {
    setError(false); setData(null);
    api.get(`/reports/registry/${report.id}/readiness`).then((r) => setData(r.data)).catch(() => setError(true));
  };
  useEffect(load, [report.id]);
  const blockers = data ? [...data.blocking, ...data.mandatory_sections_unapproved.map((s) => `Mandatory section not approved for reporting: ${s}`)] : [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="export-blocker-dialog" onCloseAutoFocus={restoreFocus}>
        <DialogHeader>
          <DialogTitle className="font-heading text-base">Export blocked — {report.id}</DialogTitle>
          <DialogDescription className="text-xs text-slate-600 leading-snug">
            "{report.title}" is not eligible for an approved export. Resolve the blocking issues below. A watermarked draft export remains available inside the report at all times.
          </DialogDescription>
        </DialogHeader>
        {!data && !error && (
          <p className="text-[11px] text-slate-400 flex items-center gap-1.5" role="status" aria-live="polite" data-testid="export-blocker-loading">
            <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />Checking export readiness…
          </p>
        )}
        {error && (
          <p className="text-[11px] text-red-600 flex items-center gap-2" role="alert" data-testid="export-blocker-error">
            Readiness check could not be loaded.
            <Button variant="outline" className="h-6 text-[10px]" onClick={load} data-testid="export-blocker-retry-btn">Retry</Button>
          </p>
        )}
        {data && (
          <ul className="space-y-1.5" aria-label="Blocking issues" data-testid="export-blocker-list">
            {blockers.map((b, i) => (
              <li key={i} className="text-[11px] text-slate-700 bg-red-50/50 border border-red-100 rounded-lg px-3 py-2 flex gap-1.5" data-testid={`export-blocker-item-${i}`}>
                <span className="text-red-500 shrink-0" aria-hidden="true">•</span>{b}
              </li>
            ))}
            {blockers.length === 0 && <li className="text-[11px] text-slate-500">Report status "{report.status}" does not permit approved export — move the report through scientific review first.</li>}
          </ul>
        )}
        <div className="flex justify-end">
          <Button variant="outline" className="h-8 text-xs" onClick={onClose} data-testid="export-blocker-close-btn">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Reports({ role, onNavigate }) {
  const [reg, setReg] = useState(null);
  const [regError, setRegError] = useState(false);
  const [tab, setTab] = useState("reports");
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [typeF, setTypeF] = useState("all");
  const [creating, setCreating] = useState(false);
  const [tplPreselect, setTplPreselect] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [blockedExport, setBlockedExport] = useState(null);

  const loadRegistry = () => {
    setRegError(false);
    api.get("/reports/registry").then((r) => setReg(r.data)).catch(() => setRegError(true));
  };
  useEffect(loadRegistry, []);
  const reports = reg?.reports || [];
  const filtered = useMemo(() => reports.filter((r) =>
    (statusF === "all" || r.status === statusF) && (typeF === "all" || r.type === typeF) &&
    (!q.trim() || `${r.title} ${r.type} ${r.audience}`.toLowerCase().includes(q.trim().toLowerCase()))), [reports, q, statusF, typeF]);

  if (regError) return (
    <div className="p-8 text-sm text-red-600 flex items-center gap-3" role="alert" data-testid="reports-load-error">
      The report registry could not be loaded.
      <Button variant="outline" className="h-8 text-xs" onClick={loadRegistry} data-testid="reports-retry-btn">Retry</Button>
    </div>
  );
  if (!reg) return <div className="p-8 text-slate-400 text-sm flex items-center gap-2" role="status" aria-live="polite" data-testid="reports-loading"><Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />Loading reports…</div>;
  const updateReport = (u) => setReg((p) => ({ ...p, reports: p.reports.map((r) => (r.id === u.id ? u : r)) }));
  const opened = reports.find((r) => r.id === openId);
  const reviewing = reports.find((r) => r.id === reviewId);

  const underReview = reports.filter((r) => r.status === "Under scientific review").length;
  const revision = reports.filter((r) => r.status === "Revision required" || (r.sections || []).some((s) => s.status === "Revision required")).length;
  const exportable = reports.filter((r) => ["Approved for reporting", "Locked final"].includes(r.status)).length;

  const doExport = (r, format = "pdf") => {
    const approved = ["Approved for reporting", "Locked final"].includes(r.status);
    if (!approved) { setBlockedExport(r); return; }
    if (r.content_key === "executive") {
      api.get(`/reports/executive/export`, { params: { format }, responseType: "blob" }).then((res) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(res.data); a.download = `${r.id}-v${r.version}.${format}`; a.click();
      });
    }
    api.post(`/reports/registry/${r.id}/exports`, { format, version_kind: "approved" })
      .then((res) => { toast.success(`Export ${res.data.export_id} recorded${r.content_key !== "executive" ? " (demonstration export)" : ""}`); })
      .catch(() => toast.error("Export failed"));
  };

  const duplicate = (r) => {
    api.post("/reports/registry", { title: `${r.title} (copy)`, type: r.type, purpose: r.purpose, audience: r.audience,
      scope: r.scope, functions: r.functions || [], output_format: r.output_format || "PDF",
      citation_style: r.citation_style || "Vancouver", sections: (r.sections || []).map((s) => s.id) })
      .then((res) => { setReg((p) => ({ ...p, reports: [...p.reports, res.data] })); toast.success(`Duplicated as ${res.data.id} (draft)`); })
      .catch(() => toast.error("Duplicate failed"));
  };

  return (
    <div className="space-y-4" data-testid="reports-view">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="reports-subtitle">Create, review and export source-traceable intelligence reports.<GovernancePopover /></p>
        <div className="flex flex-wrap items-center gap-1.5 ml-auto">
          {[["reports", "Reports"], ["review", "Review & Approvals"], ["templates", "Templates"]].map(([id, label]) => (
            <button key={id} data-testid={`reports-tab-${id}`} onClick={() => { setTab(id); setOpenId(null); }}
              className={`text-[11px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors ${tab === id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-teal-400"}`}>{label}</button>
          ))}
        </div>
      </div>

      {tab === "reports" && !opened && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px] max-w-sm">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input placeholder="Search reports…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 pl-8 text-xs" data-testid="reports-search" aria-label="Search reports" />
            </div>
            <Select value={statusF} onValueChange={setStatusF}>
              <SelectTrigger className="h-8 w-44 text-[11px]" data-testid="reports-status-filter" aria-label="Filter reports by status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all" className="text-xs">All statuses</SelectItem>
                {reg.report_statuses.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={typeF} onValueChange={setTypeF}>
              <SelectTrigger className="h-8 w-48 text-[11px]" data-testid="reports-type-filter" aria-label="Filter reports by type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all" className="text-xs">All types</SelectItem>
                {[...new Set(reports.map((r) => r.type))].map((t) => <SelectItem key={t} value={t} className="text-xs">{t}</SelectItem>)}</SelectContent>
            </Select>
            <Button className="h-8 bg-teal-600 hover:bg-teal-700 text-white text-xs ml-auto" onClick={() => { setTplPreselect(null); setCreating(true); }} data-testid="create-report-btn">
              <Plus className="w-3.5 h-3.5 mr-1" />Create report
            </Button>
          </div>

          {(underReview > 0 || revision > 0 || exportable > 0) && (
            <p className="text-[11px] text-slate-600" data-testid="attention-summary">
              {underReview > 0 && <span className="mr-3">{underReview} report{underReview > 1 ? "s" : ""} awaiting scientific review</span>}
              {revision > 0 && <span className="mr-3 text-amber-700">{revision} report{revision > 1 ? "s" : ""} with revision requests</span>}
              {exportable > 0 && <span className="text-teal-700">{exportable} approved report{exportable > 1 ? "s" : ""} available for export</span>}
            </p>
          )}

          <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm overflow-x-auto" data-testid="reports-table">
            <table className="w-full text-xs" aria-label="Report library">
              <thead><tr className="text-left border-b border-slate-200">
                {["Report name", "Type", "Audience", "Owner", "Updated", "Review status", "Version", "Actions"].map((h) => (
                  <th key={h} scope="col" className="py-2 px-3 font-mono text-[9px] uppercase tracking-widest text-slate-400 whitespace-nowrap">{h}</th>))}
              </tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`report-row-${r.id}`}>
                    <td className="py-2.5 px-3 font-semibold text-slate-800 max-w-[260px]">
                      <button onClick={() => setOpenId(r.id)} className="text-left hover:text-teal-700" data-testid={`open-report-${r.id}`}>{r.title}</button>
                      <span className="block text-[8.5px] font-mono uppercase text-amber-600/70">Illustrative demonstration data</span>
                    </td>
                    <td className="py-2.5 px-3 text-slate-600">{r.type}</td>
                    <td className="py-2.5 px-3 text-slate-600 max-w-[160px] truncate">{r.audience}</td>
                    <td className="py-2.5 px-3 text-slate-600">{r.owner}</td>
                    <td className="py-2.5 px-3 text-slate-500">{r.last_updated}</td>
                    <td className="py-2.5 px-3"><Badge variant="outline" className={`text-[9px] ${RPT_STATUS_BG[r.status]}`}>{r.status}</Badge>
                      {r.recall && (
                        <>
                          <Badge variant="outline" className={`text-[9px] ml-1 ${r.recall.status === "Recalled" ? "bg-red-50 text-red-700 border-red-300" : "bg-amber-50 text-amber-700 border-amber-300"}`}
                            data-testid={`recall-badge-${r.id}`}>{r.recall.status}</Badge>
                          {r.recall.replacement_id && (
                            <button onClick={() => setOpenId(r.recall.replacement_id)} data-testid={`recall-replacement-${r.id}`}
                              aria-label={`Open replacement version ${r.recall.replacement_id}`}
                              className="block text-[9.5px] font-semibold text-teal-700 hover:text-teal-900 mt-0.5">→ Replacement: {r.recall.replacement_id}</button>
                          )}
                        </>
                      )}
                      {r.archived && <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-300 ml-1" data-testid={`archived-badge-${r.id}`}>Archived</Badge>}
                    </td>
                    <td className="py-2.5 px-3 text-slate-600">v{r.version}</td>
                    <td className="py-2.5 px-3 whitespace-nowrap">
                      <button onClick={() => setOpenId(r.id)} title="Open preview" aria-label={`Open preview of ${r.title}`} className="text-teal-700 mr-2" data-testid={`preview-${r.id}`}><Eye className="w-3.5 h-3.5" aria-hidden="true" /></button>
                      <button onClick={() => { setReviewId(r.id); setTab("review"); }} title="Review history & approvals" aria-label={`Open review history and approvals for ${r.title}`} className="text-indigo-700 mr-2" data-testid={`history-${r.id}`}><History className="w-3.5 h-3.5" aria-hidden="true" /></button>
                      <button onClick={() => duplicate(r)} title="Duplicate" aria-label={`Duplicate ${r.title}`} className="text-slate-500 mr-2" data-testid={`duplicate-${r.id}`}><Copy className="w-3.5 h-3.5" aria-hidden="true" /></button>
                      <button onClick={() => doExport(r)} title={["Approved for reporting", "Locked final"].includes(r.status) ? "Export" : "Export (shows blocking issues)"}
                        aria-label={["Approved for reporting", "Locked final"].includes(r.status) ? `Export ${r.title}` : `Export ${r.title} — blocked, opens blocking issues`}
                        className={["Approved for reporting", "Locked final"].includes(r.status) ? "text-teal-700" : "text-slate-300"} data-testid={`export-${r.id}`}><Download className="w-3.5 h-3.5" aria-hidden="true" /></button>
                      {!r.archived && ARCHIVABLE.includes(r.status) && (
                        <button onClick={() => setArchiving(r)} title="Archive (soft — restorable)" aria-label={`Archive ${r.title} — soft archive, restorable`} className="text-amber-600 ml-2" data-testid={`archive-${r.id}`}><Archive className="w-3.5 h-3.5" aria-hidden="true" /></button>
                      )}
                      {r.archived && (
                        <>
                          <button onClick={() => api.post(`/reports/registry/${r.id}/restore`, { reason: "restore" }).then((res) => { updateReport(res.data); toast.success(`${r.id} restored from archive.`); }).catch((e) => toast.error(e.response?.data?.detail || "Restore failed"))}
                            title="Restore from archive" aria-label={`Restore ${r.title} from archive`} className="text-teal-700 ml-2" data-testid={`restore-${r.id}`}><ArchiveRestore className="w-3.5 h-3.5" aria-hidden="true" /></button>
                          <button onClick={() => api.delete(`/reports/registry/${r.id}`).then(() => { setReg((p) => ({ ...p, reports: p.reports.filter((x) => x.id !== r.id) })); toast.success(`${r.id} permanently deleted.`); }).catch((e) => toast.error(e.response?.data?.detail || "Deletion not permitted"))}
                            title={`Permanent deletion — administrators only, after retention (${r.archive?.retention_until || ""})`} aria-label={`Permanently delete ${r.title} — administrators only, after retention period`} className="text-slate-300 hover:text-red-500 ml-2" data-testid={`delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" aria-hidden="true" /></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && reports.length > 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400 text-[11px]">No reports match the current filters.</td></tr>}
                {reports.length === 0 && (
                  <tr><td colSpan={8} className="py-10 text-center" data-testid="reports-empty-state">
                    <p className="text-sm text-slate-500">No reports have been created for this assessment.</p>
                    <div className="flex justify-center gap-2 mt-3">
                      <Button className="h-8 text-xs bg-teal-600 text-white" onClick={() => setCreating(true)}>Create first report</Button>
                      <Button variant="outline" className="h-8 text-xs" onClick={() => setTab("templates")}>View templates</Button>
                    </div>
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "reports" && opened && (
        <div className="space-y-3">
          <button onClick={() => setOpenId(null)} className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800" data-testid="back-to-reports">
            <ArrowLeft className="w-3.5 h-3.5" />Back to reports
          </button>
          <ReportPreview report={opened} onUpdated={updateReport} />
        </div>
      )}

      {archiving && <ArchiveDialog report={archiving} onClose={() => setArchiving(null)} onDone={updateReport} />}
      {blockedExport && <ExportBlockerDialog report={blockedExport} onClose={() => setBlockedExport(null)} />}

      {tab === "review" && (
        <div className="space-y-3">
          <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm overflow-x-auto" data-testid="review-queue">
            <table className="w-full text-xs" aria-label="Review queue">
              <thead><tr className="text-left border-b border-slate-200">
                {["Report", "Version", "Current stage", "Assigned reviewer", "Submitted", "Due", "Issues", "Action"].map((h) => (
                  <th key={h} scope="col" className="py-2 px-3 font-mono text-[9px] uppercase tracking-widest text-slate-400 whitespace-nowrap">{h}</th>))}
              </tr></thead>
              <tbody>
                {reports.map((r) => {
                  const issues = (r.sections || []).filter((s) => s.status === "Revision required").length + (r.sections || []).reduce((a, s) => a + (s.open_issues || 0), 0);
                  return (
                    <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`queue-row-${r.id}`}>
                      <td className="py-2.5 px-3 font-semibold text-slate-800 max-w-[260px]">{r.title}</td>
                      <td className="py-2.5 px-3 text-slate-600">v{r.version}</td>
                      <td className="py-2.5 px-3"><Badge variant="outline" className={`text-[9px] ${RPT_STATUS_BG[r.status]}`}>{r.status}</Badge></td>
                      <td className="py-2.5 px-3 text-slate-600">{r.reviewer}</td>
                      <td className="py-2.5 px-3 text-slate-500">{r.generated}</td>
                      <td className="py-2.5 px-3 text-slate-500">{r.status === "Locked final" ? "—" : "Rolling"}</td>
                      <td className={`py-2.5 px-3 ${issues ? "text-amber-700 font-semibold" : "text-slate-400"}`}>{issues}</td>
                      <td className="py-2.5 px-3"><button onClick={() => setReviewId(r.id)} className="text-teal-700 font-semibold hover:underline" data-testid={`queue-open-${r.id}`}>Open</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-[10px] text-slate-400 px-3 pb-2">Lifecycle: Draft → Scientific Review → Revision if required → Approved for Reporting → Locked Final. "Approved for reporting" means content may enter the report; no status means approved for clinical use or implementation.</p>
          </div>
          {reviewing && <ReportApprovals report={reviewing} onUpdated={updateReport} onNavigate={onNavigate} />}
        </div>
      )}

      {tab === "templates" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="templates-grid">
          {TEMPLATES.map((t) => (
            <div key={t.id} className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid={`template-card-${t.id}`}>
              <p className="text-[13px] font-bold text-slate-900">{t.name}</p>
              <p className="text-[11px] text-slate-500 mt-0.5">{t.purpose}</p>
              <p className="text-[10px] text-slate-400 mt-1.5">{t.audience}</p>
              <p className="text-[9.5px] font-mono text-slate-400 mt-1">{MANDATORY.length} core + {t.recommended.length} recommended sections · {t.formats.join(" / ")} · Updated {t.updated}</p>
              <div className="flex gap-2 mt-3">
                <Button variant="outline" className="h-7 text-[11px]" onClick={() => toast.info(`${t.name}: core sections — ${MANDATORY.length}; recommended — ${t.recommended.length}. Full structure shown in the create workflow.`)}>Preview</Button>
                <Button className="h-7 text-[11px] bg-teal-600 text-white" onClick={() => { setTplPreselect(t); setCreating(true); }} data-testid={`use-template-${t.id}`}>Use template</Button>
              </div>
            </div>
          ))}
          <p className="text-[10px] text-slate-400 md:col-span-2">Template administration is managed in Admin & Review. AI-assisted briefing drafts are available through the "Open AI Analyst" utility and remain labelled AI-generated drafts until scientific review.</p>
        </div>
      )}

      {creating && <CreateReportModal reg={reg} template={tplPreselect} onClose={() => setCreating(false)}
        onCreated={(r) => setReg((p) => ({ ...p, reports: [...p.reports, r] }))} />}
    </div>
  );
}
